/**
 * Sysadmin Platform → Air-Gap (Phase 5 PR-B v1).
 *
 * Per SYSADMIN-PLATFORM-SPEC §3.8 — the load-bearing compliance feature
 * for ITAR/CMMC. Surfaces:
 *   - 4-up summary (enabled/disabled, observer health, allowlist size,
 *     last verified)
 *   - Counters strip (blocks 24h, total, test, logged-only)
 *   - Flat allowlist table with Add / Delete
 *   - Add dialog with pre-flight history panel (shows recent outbound
 *     attempts for that hostname BEFORE the operator widens the
 *     allowlist) — critical UX for "is this safe?" decisions
 *
 * Anti-pattern §11.5: the allowlist table is NOT wrapped in <Card>.
 * Anti-pattern §10.7: adding to the allowlist is kill-switch-grade
 * because it widens the air-gap surface — typed-confirm required.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Wifi, WifiOff, ShieldCheck, AlertTriangle, RefreshCw, Plus, Trash2,
  Inbox, Activity, Clock, Lock,
} from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { StatTile } from '@/components/ui/stat-tile';
import { StatusPill } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { PageError } from '@/components/ui/page-error';
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog';
import {
  useSysadminAirgap, useSysadminAllowlist, useAddAllowlistEntry,
  useRemoveAllowlistEntry, useOutboundAttempts, useSetAirgapPosture,
  type AllowlistEntry, type AirgapProtocol,
} from '@/lib/use-sysadmin-platform';

export const Route = createFileRoute('/sysadmin/platform/airgap')({
  component: AirgapPage,
});

function AirgapPage() {
  const summaryQ = useSysadminAirgap();
  const listQ = useSysadminAllowlist();

  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<AllowlistEntry | null>(null);
  // P2 T1 — the posture flip the tile now offers. Null = dialog closed.
  const [postureTarget, setPostureTarget] = useState<boolean | null>(null);

  if (summaryQ.error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageError
          title="Failed to load air-gap status"
          message={summaryQ.error.message}
          onRetry={summaryQ.refetch}
          retrying={summaryQ.isFetching}
        />
      </div>
    );
  }

  const summary = summaryQ.data;
  const entries = listQ.data?.entries ?? [];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      {/* ── Page heading ── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {summary?.enabled ? (
            <Wifi className="h-7 w-7 text-emerald-600" />
          ) : (
            <WifiOff className="h-7 w-7 text-amber-600" />
          )}
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Air-Gap
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.8</Badge>
              <Badge variant="secondary" className="text-xs">PR-B</Badge>
              <span className="text-xs text-muted-foreground">
                Egress policy + observer + allowlist roster
              </span>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            summaryQ.refetch();
            listQ.refetch();
          }}
          disabled={summaryQ.isFetching || listQ.isFetching}
        >
          <RefreshCw
            className={
              summaryQ.isFetching ? 'h-3.5 w-3.5 mr-1.5 animate-spin' : 'h-3.5 w-3.5 mr-1.5'
            }
          />
          Refresh
        </Button>
      </div>

      {/* ── 4-up summary ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Air-gap enabled"
          value={summary?.enabled ? 'Yes' : 'No'}
          icon={summary?.enabled ? ShieldCheck : AlertTriangle}
          tone={summary?.enabled ? 'success' : 'destructive'}
          // P2 T1 — the tile now SETS the posture as well as reflecting
          // it. No new tile, no new section: the control lives inside
          // the existing 4-up summary's hint line. The tile itself
          // deliberately keeps no `onClick` (a nested <button> inside a
          // button tile would be invalid markup).
          hint={
            <span className="flex items-center justify-between gap-2">
              <span>
                {summary?.enabled
                  ? 'Egress blocked by observer'
                  : 'Egress observer in log-only mode'}
              </span>
              {summary ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-[10px]"
                  onClick={() => setPostureTarget(!summary.enabled)}
                  title={
                    summary.enabled
                      ? 'Turn air-gap OFF — external connectors become creatable'
                      : 'Turn air-gap ON — external connectors are refused platform-wide'
                  }
                  data-testid="airgap-posture-toggle"
                >
                  {summary.enabled ? 'Turn OFF' : 'Turn ON'}
                </Button>
              ) : null}
            </span>
          }
        />
        <StatTile
          label="Observer"
          value={
            summary?.observer.status === 'healthy'
              ? 'Healthy'
              : summary?.observer.status === 'degraded'
                ? 'Degraded'
                : 'Down'
          }
          icon={Activity}
          tone={
            summary?.observer.status === 'healthy'
              ? 'success'
              : summary?.observer.status === 'degraded'
                ? 'warning'
                : 'destructive'
          }
          hint={
            summary?.observer.lastRunAt
              ? `Last heartbeat ${formatAge(summary.observer.ageMs)}`
              : 'No heartbeat'
          }
        />
        <StatTile
          label="Allowlist size"
          value={summary?.allowlist.activeCount ?? '—'}
          suffix={
            summary && summary.allowlist.totalCount > summary.allowlist.activeCount
              ? `/ ${summary.allowlist.totalCount} total`
              : undefined
          }
          icon={Lock}
          tone={summary && summary.allowlist.activeCount === 0 ? 'success' : 'warning'}
          hint={
            summary && summary.allowlist.activeCount === 0
              ? 'True air-gap (no exceptions)'
              : 'Active exceptions exist'
          }
        />
        <StatTile
          label="Last verified"
          value={
            summary?.lastVerifiedAt
              ? formatAge(Date.now() - new Date(summary.lastVerifiedAt).getTime())
              : 'Never'
          }
          icon={Clock}
          tone={summary?.lastVerifiedAt ? 'default' : 'warning'}
          hint={
            summary?.lastVerifiedAt
              ? new Date(summary.lastVerifiedAt).toLocaleString()
              : 'Run a synthetic test to attest the air-gap'
          }
        />
      </div>

      {/* ── 24h counters ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Blocks last 24h"
          value={summary?.counters.blocksLast24h ?? '—'}
          icon={ShieldCheck}
          tone={
            summary && summary.counters.blocksLast24h > 0 ? 'warning' : 'success'
          }
          hint="Real outbound attempts blocked"
        />
        <StatTile
          label="Total blocks"
          value={summary?.counters.totalBlocks ?? '—'}
          icon={Activity}
          tone="muted"
          hint="All-time block history"
        />
        <StatTile
          label="Test blocks 24h"
          value={summary?.counters.testBlocksLast24h ?? '—'}
          icon={Activity}
          tone="muted"
          hint="Synthetic events from operators"
        />
        <StatTile
          label="Logged-only 24h"
          value={summary?.counters.loggedOnlyLast24h ?? '—'}
          icon={Activity}
          tone="muted"
          hint="Tier-2 diagnostic (not counted as blocks)"
        />
      </div>

      {/* ── Allowlist table ── */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Lock className="h-4 w-4 text-muted-foreground" />
              Egress allowlist
            </h2>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Every (hostname, port, protocol) tuple NOT in this list is blocked. Empty list = true air-gap. Adding an entry widens the platform's trust surface — typed-confirm required.
            </p>
          </div>
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add allowlist entry
          </Button>
        </div>

        {listQ.error ? (
          <PageError
            title="Failed to load allowlist"
            message={listQ.error.message}
            onRetry={listQ.refetch}
            retrying={listQ.isFetching}
          />
        ) : listQ.isLoading && entries.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground text-sm animate-pulse">
            Loading allowlist…
          </div>
        ) : entries.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="Empty allowlist"
            body="This is the true air-gap state: nothing on this list, nothing leaves the box. Adding an entry creates a narrow exception."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hostname</TableHead>
                <TableHead>Port</TableHead>
                <TableHead>Protocol</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Added</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="text-xs font-mono">{e.hostname}</TableCell>
                  <TableCell className="text-xs font-mono">
                    {e.port ?? <span className="text-muted-foreground">any</span>}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] font-mono">
                      {e.protocol}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="truncate max-w-[260px]" title={e.reason}>
                      {e.reason}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(e.addedAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {e.isActive ? (
                      <StatusPill status="warning" compact>Active</StatusPill>
                    ) : (
                      <StatusPill status="muted" compact>Expired</StatusPill>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {e.isActive && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRemoveTarget(e)}
                        title="Remove this allowlist entry"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Remove
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      {/* ── Dialogs ── */}
      {addOpen && (
        <AddAllowlistDialog
          open={addOpen}
          onOpenChange={setAddOpen}
        />
      )}
      {removeTarget && (
        <RemoveAllowlistDialog
          target={removeTarget}
          onClose={() => setRemoveTarget(null)}
        />
      )}
      {postureTarget !== null && (
        <AirgapPostureDialog
          nextEnabled={postureTarget}
          onClose={() => setPostureTarget(null)}
        />
      )}
    </div>
  );
}

// ─── Air-gap posture typed-confirm (P2 T1 / ADR-0033 §8 D1) ──────────────
//
// Reuses the exact step-up pattern the allowlist dialogs already use:
// TypedConfirmDialog's `requireReason` collects the value sent as the
// `X-Step-Up-Reason` header, and the backend additionally requires a
// per-change `reason` in the body (min 10 chars) which lands in the
// platform audit row's details. We send the same operator text for both
// — one thing to type, two places it is recorded — because asking an
// operator for two different justifications in one dialog produces two
// copies of the same sentence.

function AirgapPostureDialog({
  nextEnabled, onClose,
}: {
  nextEnabled: boolean;
  onClose: () => void;
}) {
  const setPosture = useSetAirgapPosture();
  const phrase = nextEnabled ? 'AIRGAP ON' : 'AIRGAP OFF';

  const consequences = nextEnabled
    ? [
        'External adapters (GitHub, OneDrive, SharePoint, webhook, REST + GraphQL APIs) can no longer be created — every connector wizard refuses them with 403 AIRGAP_FORBIDDEN',
        'Forge-generated (Tier 3) MCP servers can no longer be assigned to a tenant',
        'MCP Tier 1 (in-process) and Tier 2 (sandboxed, egress-allowlisted) stay available',
        'Argus Live stops offering network connectors in the data-binding picker',
        'This flag does NOT change packet-level egress: the block lives in the Docker network / iptables layer, and the egress observer only records attempts either way',
        'EVERY tenant on this platform is affected, not just yours',
        'Connectors that already exist are not deleted — they stop being creatable',
      ]
    : [
        'External adapters (GitHub, OneDrive, SharePoint, webhook, REST + GraphQL APIs) become creatable again',
        'Forge-generated (Tier 3) MCP servers become assignable to tenants',
        'Argus Live offers network connectors in the data-binding picker again',
        'This flag does NOT open packet-level egress: if the deployment blocks outbound traffic at the Docker network / iptables layer, it still does',
        'EVERY tenant on this platform is affected, not just yours',
        'Do not do this on an ITAR / CMMC deployment: the air-gap claim on the compliance surface becomes false',
      ];

  // R2 / P2-BF-06 — the dialog stays mounted until the server answers.
  // TypedConfirmDialog used to close itself the instant Confirm was
  // pressed, which unmounted this component (and its mutation) before the
  // PATCH resolved: a 401 STEP_UP_REQUIRED from the tier-1 step-up gate,
  // a 403, or a 422 landed nowhere and the tile still showed the old
  // posture. Same shape the sibling AddAllowlistDialog already uses:
  // close on success, keep the dialog + render the error on failure.
  const handleConfirm = (
    payload: { reason?: string; secondApprover?: string } = {},
  ) => {
    const reason = payload.reason?.trim() ?? '';
    setPosture.mutate(
      {
        input: { enabled: nextEnabled, reason, confirmed: true },
        stepUpReason: reason,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <TypedConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      closeOnConfirm={false}
      pending={setPosture.isPending}
      errorMessage={setPosture.error ? setPosture.error.message : null}
      title={nextEnabled ? 'Turn air-gap ON' : 'Turn air-gap OFF'}
      description={
        nextEnabled
          ? 'Air-gap mode makes the platform refuse every connector that needs public-internet egress, for every tenant on this box. Turn it on for ITAR / CMMC deployments and any install that must not initiate external network calls.'
          : 'Turning air-gap OFF lets tenants create connectors that reach the public internet. This is the correct posture for a connected-mode install (web search, GitHub, Microsoft 365) and the wrong one for an ITAR / CMMC deployment.'
      }
      consequences={consequences}
      confirmPhrase={phrase}
      confirmLabel={
        setPosture.isPending
          ? 'Applying…'
          : nextEnabled
            ? 'Turn air-gap ON'
            : 'Turn air-gap OFF'
      }
      requireReason
      // The backend's AirgapPostureSchema requires `reason.min(10)`, so
      // the client gate matches it — otherwise Confirm would enable on a
      // reason the server then rejects with a 400 the operator could not
      // have predicted.
      reasonMinLength={10}
      reasonPlaceholder="Audit reason: ticket / change request / deployment posture decision (10-500 chars)"
      onConfirm={handleConfirm}
    />
  );
}

// ─── Add allowlist dialog with pre-flight history ────────────────────────

function AddAllowlistDialog({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [hostname, setHostname] = useState('');
  const [port, setPort] = useState('');
  const [protocol, setProtocol] = useState<AirgapProtocol>('tcp');
  const [reason, setReason] = useState('');
  const [confirmStage, setConfirmStage] = useState(false);
  const add = useAddAllowlistEntry();

  // Pre-flight: fetch recent outbound attempts matching the hostname.
  // Enabled only when the hostname field has 3+ chars so we don't
  // hammer the endpoint on every keystroke.
  const trimmedHostname = hostname.trim();
  const preFlightEnabled = trimmedHostname.length >= 3;
  const preflight = useOutboundAttempts({
    hostname: trimmedHostname || undefined,
    limit: 10,
    windowHours: 72,
    enabled: preFlightEnabled,
  });

  const hostValid = useMemo(
    () => /^[a-zA-Z0-9._:-]+$/.test(trimmedHostname) && trimmedHostname.length > 0,
    [trimmedHostname],
  );
  const reasonValid = reason.trim().length >= 3;
  const portValid =
    port.trim() === '' ||
    (/^\d+$/.test(port.trim()) &&
      parseInt(port, 10) >= 1 &&
      parseInt(port, 10) <= 65535);

  const canProceed = hostValid && reasonValid && portValid;

  const handleProceed = () => {
    if (!canProceed) return;
    setConfirmStage(true);
  };

  // #1131 — TypedConfirmDialog's `requireReason` collects the step-up
  // reason for the X-Step-Up-Reason header. The body's `reason` field
  // (the per-rule justification, captured separately in this dialog's
  // textarea) is what the rule itself records; the step-up reason is
  // the operator's session-level audit anchor.
  const handleConfirm = (
    payload: { reason?: string; secondApprover?: string } = {},
  ) => {
    const stepUpReason = payload.reason?.trim() ?? '';
    add.mutate(
      {
        input: {
          hostname: trimmedHostname,
          port: port.trim() === '' ? null : parseInt(port, 10),
          protocol,
          reason: reason.trim(),
          confirmed: true,
        },
        stepUpReason,
      },
      {
        onSuccess: () => {
          setConfirmStage(false);
          onOpenChange(false);
          // Reset for next time.
          setHostname('');
          setPort('');
          setReason('');
          setProtocol('tcp');
        },
        onError: () => {
          // Keep the typed-confirm dialog open so the operator sees the
          // server error and can retry without re-typing.
          setConfirmStage(false);
        },
      },
    );
  };

  if (confirmStage) {
    return (
      <TypedConfirmDialog
        open
        onOpenChange={(o) => !o && setConfirmStage(false)}
        title="Confirm air-gap exception"
        description={`Adding ${trimmedHostname}${port ? `:${port}` : ''} (${protocol}) to the allowlist widens the platform's trust surface. The egress observer will permit this traffic for every tenant on this box.`}
        consequences={[
          `Hostname ${trimmedHostname} (port ${port || 'any'}, protocol ${protocol}) bypasses the egress block`,
          'Every tenant on this platform shares this exception',
          'The egress observer reads this list within 30s of insert',
          'A platform_audit_logs row records this with highRiskAction=true',
          'Remove the entry to restore the air-gap invariant',
        ]}
        confirmPhrase={trimmedHostname}
        confirmLabel={add.isPending ? 'Adding…' : 'Add to allowlist'}
        // #1131 — collect the session-level step-up audit reason from
        // the dialog's reason textarea. This becomes the
        // X-Step-Up-Reason header. The body's per-rule `reason`
        // field above is separate.
        requireReason
        reasonPlaceholder="Audit reason: ticket / change request / incident reference (3-500 chars)"
        onConfirm={handleConfirm}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add egress allowlist entry</DialogTitle>
          <DialogDescription>
            Define the hostname + port + protocol you want to permit. The next step shows recent outbound attempts so you can verify the destination matches real traffic before widening the air-gap.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5 col-span-2">
            <label className="text-xs font-semibold">Hostname</label>
            <Input
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              placeholder="api.example.internal"
              className="h-9 text-sm font-mono"
            />
            {hostname.length > 0 && !hostValid && (
              <p className="text-[10px] text-destructive">
                Hostname must contain only letters, digits, dots, hyphens, colons, or underscores.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold">Port (blank = any)</label>
            <Input
              value={port}
              onChange={(e) => setPort(e.target.value)}
              placeholder="443"
              className="h-9 text-sm font-mono"
            />
            {port.length > 0 && !portValid && (
              <p className="text-[10px] text-destructive">1-65535 only.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold">Protocol</label>
            <Select value={protocol} onValueChange={(v) => setProtocol(v as AirgapProtocol)}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tcp">tcp</SelectItem>
                <SelectItem value="udp">udp</SelectItem>
                <SelectItem value="http">http</SelectItem>
                <SelectItem value="https">https</SelectItem>
                <SelectItem value="any">any</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 col-span-2">
            <label className="text-xs font-semibold">
              Reason <span className="text-muted-foreground">(audit trail)</span>
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Forge code-gen needs read access to <vendor> docs API"
              className="min-h-[60px] text-sm"
            />
          </div>
        </div>

        {/* Pre-flight history panel */}
        {preFlightEnabled && (
          <div className="rounded-md border bg-muted/30 p-3 space-y-2">
            <div className="text-xs font-semibold flex items-center gap-2">
              <Activity className="h-3.5 w-3.5" />
              Pre-flight: recent outbound attempts (last 72h)
            </div>
            <p className="text-[10px] text-muted-foreground">
              Has anything on this platform actually tried to reach this host? Patterns here are the "Yes, this is real traffic" signal.
            </p>
            {preflight.isLoading ? (
              <div className="text-xs text-muted-foreground animate-pulse py-2">
                Loading recent attempts…
              </div>
            ) : preflight.error ? (
              <div className="text-xs text-destructive">
                {preflight.error.message}
              </div>
            ) : (preflight.data?.attempts ?? []).length === 0 ? (
              <div className="text-xs text-muted-foreground italic py-2">
                No recent outbound attempts matched. Either this is a brand-new path or the hostname filter doesn't match the actual destination. Verify the spelling before adding.
              </div>
            ) : (
              <div className="text-[11px] font-mono space-y-1 max-h-[140px] overflow-y-auto">
                {preflight.data!.attempts.map((a) => (
                  <div key={a.id} className="flex items-center gap-2">
                    <span className="text-muted-foreground tabular-nums">
                      {new Date(a.blockedAt).toLocaleTimeString()}
                    </span>
                    <span>{a.destination}</span>
                    <span className="text-muted-foreground">
                      :{a.port ?? '*'} {a.protocol ?? '?'}
                    </span>
                    {a.isTest && (
                      <Badge variant="outline" className="text-[9px]">
                        test
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {add.error && (
          <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
            {add.error.message}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleProceed}
            disabled={!canProceed}
          >
            Continue to confirmation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Remove allowlist typed-confirm ──────────────────────────────────────

function RemoveAllowlistDialog({
  target, onClose,
}: {
  target: AllowlistEntry;
  onClose: () => void;
}) {
  const remove = useRemoveAllowlistEntry();
  const phrase = target.port
    ? `${target.hostname}:${target.port}`
    : target.hostname;

  // #1131 — collect the X-Step-Up-Reason via TypedConfirmDialog's
  // requireReason field. The mutation factory forwards it as the
  // header automatically.
  const handleConfirm = (
    payload: { reason?: string; secondApprover?: string } = {},
  ) => {
    remove.mutate(
      {
        input: { id: target.id, confirmed: true },
        stepUpReason: payload.reason?.trim() ?? '',
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <TypedConfirmDialog
      open
      onOpenChange={(o) => !o && onClose()}
      // R2 (same gap as AirgapPostureDialog, named in the same finding):
      // this dialog also unmounted on Confirm, so a 401 STEP_UP_REQUIRED
      // on a removal — which narrows the trust surface back and is itself
      // an audited compliance action — was silent.
      closeOnConfirm={false}
      pending={remove.isPending}
      errorMessage={remove.error ? remove.error.message : null}
      title="Remove allowlist entry"
      description={`This restores the air-gap invariant for ${target.hostname}${target.port ? `:${target.port}` : ''} (${target.protocol}). Any active traffic to this destination will start failing on the next observer cycle (within 30s).`}
      consequences={[
        `Entry for ${target.hostname} ${target.port ? `:${target.port}` : ''} expires immediately`,
        'The egress observer picks up the change within 30s',
        'In-flight connections may interrupt',
        'The row stays in the DB for audit replay (soft-delete)',
      ]}
      confirmPhrase={phrase}
      confirmLabel={remove.isPending ? 'Removing…' : 'Remove from allowlist'}
      requireReason
      reasonPlaceholder="Audit reason: ticket / change request / incident reference (3-500 chars)"
      onConfirm={handleConfirm}
    />
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function formatAge(ageMs: number | null): string {
  if (ageMs === null) return '—';
  if (ageMs < 0) return 'just now';
  if (ageMs < 1_000) return 'just now';
  if (ageMs < 60_000) return `${Math.floor(ageMs / 1_000)}s ago`;
  if (ageMs < 3_600_000) return `${Math.floor(ageMs / 60_000)}m ago`;
  if (ageMs < 86_400_000) return `${Math.floor(ageMs / 3_600_000)}h ago`;
  return `${Math.floor(ageMs / 86_400_000)}d ago`;
}
